import { Component, ErrorInfo, PropsWithChildren } from 'react';

import { IoBugOutline } from 'react-icons/io5';

import { BootError } from './BootError';

type ErrorBoundaryState = {
  error: Error | null;
};

/**
 * The app's last line: without one of these a throw anywhere in the tree unmounts
 * the whole of it, and what the user is left looking at is either a blank page or —
 * when the throw happens before React's first commit — the static shell from
 * index.html, which is the splash. Both are indistinguishable from a hang, and
 * neither can be recovered from without the user knowing to reload by hand.
 */
class ErrorBoundary extends Component<PropsWithChildren, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept even once a reporter is wired in: a boundary that swallows the error
    // silently makes the one screenshot a user sends back say nothing at all.
    console.error('Unhandled error, caught by the root boundary.', error, info);
  }

  public render() {
    if (this.state.error === null) {
      return this.props.children;
    }

    // A reload rather than clearing the error: whatever threw did so against state
    // this boundary cannot see, and re-rendering the same tree onto it would most
    // likely throw again.
    return (
      <BootError
        // Not the connection this time, so not the connection's mark.
        icon={<IoBugOutline />}
        title="¡Ups! Algo ha ido mal"
        message="La aplicación se ha encontrado con un error inesperado. Vuelve a cargarla para continuar."
        retryLabel="Recargar"
        onRetry={() => {
          window.location.reload();
        }}
      />
    );
  }
}

export { ErrorBoundary };
